import axios, { makePostApiCall } from '@/plugins/axios'
import type { ApiEndPointMappingKey } from './types'
import { apiEndPointMapping } from './types'
import { config } from '@/config/index.ts'
import type { DecodedToken } from '@/utils/jwtHelpers.ts'
import { extractFieldsFromToken } from '@/utils/jwtHelpers.ts'
import type { SearchFilterOption, SuggestRecord } from '@/components/Search/types.ts'
import { getLocalLanguageISOCode } from '@/plugins/i18n.ts'

import {
  OPENSEARCH_RESULT_WINDOW,
  STANDARDS_RECORDS_PER_PAGE,
  MY_COLLECTION_NAME
} from '@/components/Search/constants.ts'

let stellarToken: string | null = null
const JWT_PATTERN = /[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/

const parseApiData = (data: any) => {
  if (typeof data !== 'string') {
    return data
  }
  try {
    return JSON.parse(data)
  } catch {
    return data
  }
}

const extractApiToken = (data: any): string => {
  const parsedData = parseApiData(data)
  if (typeof parsedData === 'string') {
    const tokenFromJsonString = parsedData.match(/"(?:token|access_token)"\s*:\s*"([^"]+)"/)?.[1]
    return (tokenFromJsonString || parsedData.match(JWT_PATTERN)?.[0] || '').trim()
  }
  if (!parsedData || typeof parsedData !== 'object') {
    return ''
  }

  const token = parsedData.token || parsedData.access_token
  if (token) {
    return String(token).trim()
  }

  return extractApiToken(parsedData.data) || extractApiToken(parsedData.results)
}

const extractTokenFromAxiosResponse = (response: any): string => {
  return (
    extractApiToken(response?.data) ||
    extractApiToken(response?.request?.responseText) ||
    extractApiToken(response?.request?.response) ||
    ''
  )
}

export const apiGetFilterOptions = async (type: ApiEndPointMappingKey) => {
  const mappedField = apiEndPointMapping[type]
  if (!mappedField) {
    return []
  }

  const response = await axios.get({
    endpoint: '/management/search/field/' + mappedField,
  })
  const data = parseApiData(response.data)

  const results = data?.results || data?.data?.results
  if (results) {
    if (type == 'collections.tags') {
      const options = results
      const tagOptions: { id: string; text: string }[] = []
      options.forEach((eleOption: { text: string }) => {
        tagOptions.push({ id: eleOption['text'], text: eleOption['text'] })
      })
      return tagOptions
    } else {
      return results
    }
  } else {
    return []
  }
}

export const apiSearchResults = async (
  endPoint: string,
  params: any,
  searchApiAbortController?: AbortController,
) => {
  const options: any = searchApiAbortController ? { signal: searchApiAbortController.signal } : {}
  const response: any = await makePostApiCall(
    '/management/services.v1.searchajax/' + endPoint,
    params,
    options,
  )
  return response?.data ?? []
}

export const apiGetStellarSearchToken = async () => {
  if (stellarToken) {
    return stellarToken // ✅ reuse cached token
  }
  const response = await axios.get({ endpoint: '/management/search/getStellarSearchToken' })
  const token = extractTokenFromAxiosResponse(response)

  if (token) {
    stellarToken = String(token)
    return stellarToken
  }
  return null
}

export const apiGetSearchFilterOptions = async () => {
  const token = await apiGetStellarSearchToken()
  if (token) {
    const language = getLocalLanguageISOCode() || 'en'
    const response = await axios.get({
      endpoint: `/api/search/opensearch/OpenSearchFilters?filterType=&language=${encodeURIComponent(language)}`,
      baseURL: config.searchBaseUrl,
      options: {
        withCredentials: false,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    })
    if (response.data && response.data.status === 200 && response.data.data) {
      return response.data.data
    } else {
      console.log('stellar token response error')
      throw new Error('Stellar token response error')
    }
  } else {
    console.log('Empty stellar token')
    throw new Error('Empty stellar token')
  }
}

const clampPageToResultWindow = (pageNo: number, recordsPerPage: number) => {
  if (recordsPerPage <= 0) return pageNo
  const maxAllowedPage = Math.max(1, Math.floor(OPENSEARCH_RESULT_WINDOW / recordsPerPage))
  if (pageNo < 1) return 1
  return Math.min(pageNo, maxAllowedPage)
}

export const apiStandardSearch = async (params: any, abortController: AbortController) => {
  const token: string | null = await apiGetStellarSearchToken()
  //extract sub_id and group_ids from token and add to params
  const { sub_id, group_ids, my_own_coll_id } = extractFieldsFromToken(token, [
    'sub_id',
    'group_ids',
    'my_own_coll_id'
  ]) as DecodedToken
  params['sub_id'] = sub_id ?? ''
  //converts string like '"[16779729 ,16779722]"' to '16779729,16779722'
  if (group_ids) {
    if (Array.isArray(group_ids)) {
      params['group_id'] = group_ids.join(',')
    } else if (typeof group_ids === 'string') {
      params['group_id'] = group_ids.replace(/[\[\]\s"]/g, '')
    }
  } else {
    params['group_id'] = ''
  }

  //only set icsCode as standards api only considers icsCode for filtering
  if ((params['icsCode'] || '').trim() === '') {
    if ((params['icsCodeLevel3'] || '').trim() !== '') {
      params['icsCode'] = params['icsCodeLevel3']
    } else if ((params['icsCodeLevel2'] || '').trim() !== '') {
      params['icsCode'] = params['icsCodeLevel2']
    } else if ((params['icsCodeLevel1'] || '').trim() !== '') {
      params['icsCode'] = params['icsCodeLevel1']
    }
  }
  
  //unset icsCodeLevel1, icsCodeLevel2, icsCodeLevel3 to avoid confusion
  delete params['icsCodeLevel1']
  delete params['icsCodeLevel2']
  delete params['icsCodeLevel3']

  //set active collection id from token if its empty
  //might be empty if myCollection is not created when vue variable are initialized in i2i
  const activeCollectionId = params.activeCollectionId?.toString().trim();
  if (!activeCollectionId || activeCollectionId === '0') {
    params.activeCollectionId = my_own_coll_id ?? '';
    params.activeCollectionName = MY_COLLECTION_NAME;
  }

  const clientRecordsPerPage =
    Number(params._clientRecordsPerPage) ||
    Number(params.recordsPerPage) ||
    STANDARDS_RECORDS_PER_PAGE
  if ('_clientRecordsPerPage' in params) {
    delete params._clientRecordsPerPage
  }
  const requestedPage = Number(params.page) || 1
  params.page = String(clampPageToResultWindow(requestedPage, clientRecordsPerPage))

  if (token) {
    const response = await axios.post({
      endpoint: '/api/search/standards',
      baseURL: config.searchBaseUrl,
      params,
      options: {
        withCredentials: false,
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
        signal: abortController.signal,
      },
    })
    if (response.data && response.data.status === 200 && response.data.data) {
      return response.data.data
    } else {
      console.log('Invalid response from Stellar Search')
      throw new Error('Invalid response from Stellar Search')
    }
  } else {
    console.log('Empty stellar token')
    throw new Error('Empty stellar token')
  }
}

export const apiGetICSCodeOptions = async (icsCode: string) => {
  const response = await axios.get({ endpoint: '/management/search/ajax_getNextICS/' + icsCode })
  if (response.data) {
    let options: SearchFilterOption[] = []
    if (response.data.data && response.data.data.labels) {
      const codes = response.data.data.codes
      const labels = response.data.data.labels
      labels.forEach((label: string, index: number) => {
        options.push({ id: codes[index], text: label })
      })
    }
    return options
  } else {
    return []
  }
}

export const apiSaveSearch = async (
  searchUrl: string,
  params: any,
  saveSearchApiAbortController?: AbortController,
) => {
  const options: any = saveSearchApiAbortController
    ? { signal: saveSearchApiAbortController.signal }
    : {};
  const response: any = await makePostApiCall(searchUrl, params, options);
  return response?.data ?? [];
};

/**
 * Standards suggest API
 * POST {searchBaseUrl}/api/search/suggest
 */
export const apiSuggestStandards = async (
  query: string,
  abortController?: AbortController,
  field: string = 'omni'
): Promise<SuggestRecord[]> => {
  const token: string | null = await apiGetStellarSearchToken();

  if (!token) {
    console.log('Empty stellar token');
    throw new Error('Empty stellar token');
  }

  // extract sub_id from the JWT so we can send subId as API expects
  const { sub_id } = extractFieldsFromToken(token, ['sub_id']) as DecodedToken;

  const payload = {
    query,
    field,
    sort: 'hits',
    subId: String(sub_id ?? ''),
    language: getLocalLanguageISOCode() || 'en'
  };

  const response = await axios.post({
    endpoint: '/api/search/suggest',
    baseURL: config.searchBaseUrl,
    params: payload,
    options: {
      withCredentials: false,
      headers: {
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache'
      },
      signal: abortController?.signal
    }
  });

  if (response.data && response.data.status === 200 && response.data.data) {
    return response.data.data.records ?? [];
  } else {
    console.log('Invalid response from Stellar Suggest');
    throw new Error('Invalid response from Stellar Suggest');
  }
};
